import {Avatar, Image} from 'antd'
import {course_getSubjectById_Api} from 'apis'
import {getId} from 'apis/model/base'
import classNames from 'classnames'
import dayjs from 'dayjs'
import getAvatar from 'helpers/getAvatar'
import getLinkToDetail, {getAbsoluteLinkToDetail} from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import {hasExpired} from 'helpers/datetimeHelpers'
import {copyLink} from 'helpers'
import {truncateMiddle} from 'helpers/stringUtils'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {useAppConfig} from 'modules/local/AppConfigContext'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useEffect, useRef, useState} from 'react'
import {BiSolidChurch} from 'react-icons/bi'
import {BsInfoCircle} from 'react-icons/bs'
import {HiOutlineAcademicCap, HiViewfinderCircle} from 'react-icons/hi2'
import {IoIosCopy} from 'react-icons/io'
import {MdAccessTime, MdEventBusy, MdSchool} from 'react-icons/md'
import {PiBookOpenTextFill, PiMedalFill, PiPencilSimpleLineFill} from 'react-icons/pi'
import {FiMapPin} from 'react-icons/fi'
import {Link} from 'react-router-dom'
import {FullSideModalLayout} from 'components/Modal/SideModalLayout'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import LoadingBlock from 'views/Loading/LoadingBlock'
import {CustomEditorRenderer} from 'views/AppLocals/custom/CustomRichEditor'
import {QRComponent} from 'views/AppLocals/custom/QRComponent'
import {renderDayOfWeek} from 'views/AppLocals/Church/functions/renderDayOfWeek'
import {NullAsDefault, renderIf, renderIfElse, renderWhere} from 'views/Shared'
import {isRawContentEmpty} from 'views/SlateEditor/functions/parseRawContent'
import CourseContext from '../../CourseContext'
import {WalkInEnrollmentModal} from 'views/Workspace/Course/components/WalkInEnrollmentModal'
import {GoDotFill} from "react-icons/go";
import {TbListDetails} from "react-icons/tb";
import {FaChalkboardTeacher} from "react-icons/fa";
import {FaUserTie} from "react-icons/fa6";
import getAliasName from "../../../../../helpers/getAliasName";

const OwnerCard = ({item}) => {
    const t = useTranslate()
    const {org_type} = item ?? {}
    const {org_types = {}} = useAppConfig('org_types')
    const type = _.get(org_types, org_type)

    return (
        <div className="flex items-center gap-3">
            <Avatar
                size={44}
                src={getAvatar(item)}
                style={{borderRadius: 10, flexShrink: 0}}
                className="flex flex-center background-200 border border-color-50"
                icon={<BiSolidChurch size={20} className="text-color-500"/>}
            />
            <div className="flex flex-col flex-1 min-w-0">
                <Link
                    to={getLinkToDetail(item)}
                    className="font-semibold text-sm text-color-000 truncate no-underline leading-snug">
                    {getTitle(item)}
                </Link>
                {renderIf(
                    type,
                    <span className="text-xs text-blue-500 italic leading-snug">
                        {t(type)}
                    </span>
                )}
            </div>
        </div>
    )
}

/* Status-only — no button; the sticky CourseSubscriptionBar handles the action */
const EnrollmentStatus = () => {
    const t = useTranslate()
    const {item} = useContext(CourseContext)
    const {is_expired, last_enrollment_time} = item ?? {}

    const isEnded = Boolean(is_expired || hasExpired(last_enrollment_time))

    let icon, label, labelClass

    if (last_enrollment_time === null) {
        icon = <MdSchool size={15} className="text-primary flex-shrink-0"/>
        label = t('register for the course')
        labelClass = 'text-primary'
    } else if (is_expired) {
        icon = <MdEventBusy size={15} className="text-color-400 flex-shrink-0"/>
        label = t('course is over')
        labelClass = 'text-color-400'
    } else if (dayjs(last_enrollment_time).isBefore(dayjs())) {
        icon = <MdEventBusy size={15} className="text-red-500 flex-shrink-0"/>
        label = t('registration has ended')
        labelClass = 'text-red-500'
    } else {
        icon = <MdAccessTime size={15} className="text-green-500 flex-shrink-0"/>
        label = t('register for the course')
        labelClass = 'text-green-500'
    }

    return (
        <div className="flex items-center gap-2">
            {icon}
            <span className={classNames('font-medium leading-snug', labelClass)}>
                {label}
            </span>
        </div>
    )
}

const CourseDateTimeline = ({time_open, time_close, last_enrollment_time, student_quota, student_count}) => {
    const t = useTranslate()
    const now = dayjs()
    const start = time_open ? dayjs(time_open) : null
    const end = time_close ? dayjs(time_close) : null
    const reg = last_enrollment_time ? dayjs(last_enrollment_time) : null

    if (!start && !end) return null

    let percent, status
    if (!start || now.isBefore(start)) {
        percent = 0
        status = 'upcoming'
    } else if (!end || now.isAfter(end)) {
        percent = 100
        status = 'ended'
    } else {
        percent = Math.round(now.diff(start) / end.diff(start) * 100)
        status = 'active'
    }

    const STATUS_CFG = {
        upcoming: {label: t('not started yet'), badgeCls: 'text-slate-400', barCls: '#e2e8f0'},
        active: {
            label: t('in progress'),
            badgeCls: 'text-blue-500',
            barCls: 'linear-gradient(to right, #63b3ed, #3182ce)'
        },
        ended: {label: t('ended'), badgeCls: 'text-green-500', barCls: 'linear-gradient(to right, #68d391, #38a169)'},
    }
    const {label, badgeCls, barCls,} = STATUS_CFG[status]

    const regEnded = reg && now.isAfter(reg)

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2">
                {reg && (
                    <div className="flex items-center gap-1">
                        <GoDotFill size={10} className="text-slate-400 flex-shrink-0"/>
                        <span className="text-xs text-color-400">{`${t('last enrollment time')} :`}</span>
                        <span
                            className={regEnded ? 'font-medium text-red-400 line-through' : 'font-bold text-slate-800'}>
                            {reg?.format('DD/MM/YYYY')}
                        </span>
                    </div>
                )}
                {student_quota && (
                    <span className="flex items-center gap-1">
                        <GoDotFill size={10} className="text-slate-400 flex-shrink-0"/>
                        <span className="text-xs text-color-400">{`${t('student quota')} :`}</span>
                        <span
                            className="font-semibold text-slate-700">{student_count}/{student_quota} {t('students')}</span>
                    </span>
                )}
                <div className="w-full border-t border-slate-200 "/>
                <div className="w-full mt-7 relative">
                    <div className="absolute left-0 right-0 w-fit flex justify-center"
                         style={{
                             top: -31,
                             left: percent < 3 ? 5 : `${Math.max(0, Math.min(percent, 97))}%`,
                             transform: 'translateX(-50%)'
                         }}>
                        <div
                            className={classNames("flex flex-col", percent === 100 ? 'items-end' : percent > 4 ? 'items-center' : 'items-start')}>
                            <span className="font-bold text-xs text-blue-700 tracking-wider">
                                {`${percent}%`}
                            </span>
                            <svg
                                width={18}
                                height={9}
                                viewBox="0 0 128 82"
                                preserveAspectRatio="none">
                                <path
                                    fill="#63b3ed"
                                    d="M 10 0 H 118 C 126 0 130 9 125 15 L 66 78 C 62 83 57 83 53 78 L 3 15 C -2 9 2 0 10 0 Z"
                                />
                            </svg>
                        </div>
                    </div>
                    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden relative">
                        <div
                            style={{
                                width: `${percent}%`,
                                background: barCls
                            }}
                            className={classNames("h-full transition-all duration-300", percent < 98 ? 'rounded-l-full' : 'rounded-full')}
                        />
                    </div>
                </div>
                <div className="w-full flex items-center justify-between">
                    <div className="flex flex-col">
                        <span className="font-bold text-gray-800 tracking-wide">
                            {start ? start.format('DD/MM/YYYY') : '—'}
                        </span>
                        <span className="font-medium text-2xs text-slate-400 tracking-wider">
                            {t('start')}
                        </span>
                    </div>
                    <div className="flex flex-col items-end">
                        <span className="font-bold text-gray-800 tracking-wide">
                            {end ? end.format('DD/MM/YYYY') : '—'}
                        </span>
                        <span className="font-medium text-2xs text-slate-400 tracking-wider">
                            {t('end')}
                        </span>
                    </div>
                </div>
            </div>
            {renderIf(
                reg,
                <div className="flex flex-col items-center tracking-wider">
                    {renderIfElse(
                        regEnded,
                        <span className="text-xs text-red-400 italic">({t('registration has ended')})</span>,
                        <EnrollmentStatus/>
                    )}
                </div>
            )}


            {/*<div className="flex flex-col gap-1 px-3 py-2">*/}
            {/*    /!* Top *!/*/}
            {/*    <div className={`flex items-center justify-between gap-2 tracking-wider ${badgeCls}`}>*/}
            {/*        <span className="font-medium text-xs">*/}
            {/*            {label}*/}
            {/*        </span>*/}
            {/*        <span className="font-semibold">*/}
            {/*            {percent}%*/}
            {/*        </span>*/}
            {/*    </div>*/}
            {/*    /!* Progress bar *!/*/}
            {/*    <div className="w-full h-2 bg-slate-200 rounded-full">*/}
            {/*        <div*/}
            {/*            style={{*/}
            {/*                width: `${percent}%`,*/}
            {/*                background: `${barCls}`*/}
            {/*            }}*/}
            {/*            className="h-full rounded-full"*/}
            {/*        />*/}
            {/*    </div>*/}
            {/*    /!* Bottom *!/*/}
            {/*    <div className="flex items-center justify-between font-medium text-xs text-slate-500 tracking-wider">*/}
            {/*        <span>{start ? start.format('DD/MM/YYYY') : '—'}</span>*/}
            {/*        <span>{end ? end.format('DD/MM/YYYY') : '—'}</span>*/}
            {/*    </div>*/}
            {/*</div>*/}

        </div>
    )
}

// Header 2 tầng (icon badge + eyebrow màu + title đậm) - style thống nhất cho MỌI section trong
// tab overview (About/Khung chương trình/Các lớp tuyển sinh/Ban giảng huấn), thay hẳn
// HeaderWithIconLine cũ (icon + title uppercase + line) để đồng bộ toàn tab.
const SectionEyebrowHeader = ({icon, iconClassName, eyebrow, eyebrowClassName, title, manageLink}) => {
    const t = useTranslate()
    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
                <div className={classNames('w-6 h-6 flex flex-center rounded-md flex-shrink-0', iconClassName)}>
                    {icon}
                </div>
                <span className={classNames('font-bold text-2xs uppercase tracking-widest', eyebrowClassName)}>
                    {t(eyebrow)}
                </span>
                {renderWhere(
                    manageLink,
                    () => (
                        <Link to={manageLink}
                              className="ml-auto text-xs text-color-300 hover:text-secondary-600 italic no-underline">
                            {`${t('manage')} ›`}
                        </Link>
                    )
                )}
            </div>
            <span className="font-bold text-base text-color-000 tracking-wide">
                {t(title)}
            </span>
        </div>
    )
}

// Card preview cho 1 môn học trong Khung chương trình - CHỈ số thứ tự + tên môn + mô tả ngắn, bám
// sát mockup thiết kế (không mã môn/tín chỉ/bắt buộc/xem nhanh như renderItem đầy đủ của
// CourseSubjectTab.js - những field đó dành cho tab "Môn học" quản trị, không cần ở preview này).
// Nội dung chi tiết 1 môn - tải LƯỜI lúc bấm vào card (KHÔNG kéo sẵn description/about vào payload
// tổng quan khóa, 2 field này có thể dài) qua course_getSubjectById_Api (SubjectController@getById,
// đã có sẵn từ trước, không cần API mới) - trả về SubjectTransformer đầy đủ (title/description/
// about/number_of_sessions), khác SubjectTransformerTiny gọn dùng cho card preview.
const SubjectDetailDrawer = ({subject_id, onCancel}) => {
    const t = useTranslate()
    const [detail, setDetail] = useState(null)

    const {handleAsyncAction: fetchSubject, isLoading} = useAsyncAction({
        apiInfo: course_getSubjectById_Api,
        onSuccess: (result, data) => setDetail(_.get(data, 'response.data', null)),
    })

    useEffect(() => {
        if (subject_id) fetchSubject({}, {id: subject_id})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subject_id])

    return (
        <FullSideModalLayout
            right={true}
            title={
                <div className="flex items-center gap-2">
                    <PiBookOpenTextFill size={16} className="text-indigo-500"/>
                    <span className="font-bold text-color-000 tracking-wide">{t('subject details')}</span>
                </div>
            }
            onCancel={onCancel}>
            <div className="px-3 pb-6 flex flex-col gap-3">
                {isLoading || detail === null ? <LoadingBlock/> : (
                    <>
                        <h2 className="font-bold text-lg text-color-000 leading-snug">{detail.title}</h2>
                        {renderIf(
                            detail.number_of_sessions,
                            <span className="w-fit px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 text-xs font-semibold">
                                {t('number of sessions count', {count: detail.number_of_sessions})}
                            </span>
                        )}
                        {renderIf(
                            detail.description,
                            <p className="text-sm text-color-300 leading-relaxed">{detail.description}</p>
                        )}
                        {renderIfElse(
                            !isRawContentEmpty(detail.about),
                            <div className="pt-3 border-t border-color-50">
                                <div className="leading-7 text-sm text-color-200" style={{lineHeight: '1.75'}}>
                                    <CustomEditorRenderer rawValue={detail.about} className="border-transparent"/>
                                </div>
                            </div>,
                            <p className="text-sm text-color-400 italic">{t('no subject content declared yet')}</p>
                        )}
                    </>
                )}
            </div>
        </FullSideModalLayout>
    )
}

// Card preview 1 môn trong "Khung chương trình" - hiện thêm số tiết học (theo yêu cầu), bấm vào mở
// Drawer xem nội dung đầy đủ (SubjectDetailDrawer) thay vì chỉ đọc được đúng 1 dòng mô tả ngắn ở đây.
const CourseSubjectPreviewCard = ({item, index}) => {
    const t = useTranslate()
    const {subject, number_of_sessions} = item ?? {}
    const [viewing, setViewing] = useState(false)

    return (
        <>
            <div
                onClick={() => setViewing(true)}
                className="p-3 md:p-4 bg-slate-50 hover:bg-slate-100 rounded-xl flex flex-col gap-1 cursor-pointer transition-colors">
                <span className="font-bold text-sm text-color-000 leading-snug">
                    {`${index + 1}. ${getTitle(subject)}`}
                </span>
                {renderIf(
                    number_of_sessions,
                    <span className="text-xs text-color-300">
                        {t('number of sessions count', {count: number_of_sessions})}
                    </span>
                )}
                {renderIf(
                    subject?.description,
                    <span className="text-xs text-color-300 leading-relaxed">
                        {subject?.description}
                    </span>
                )}
            </div>
            {viewing && (
                <SubjectDetailDrawer subject_id={getId(subject)} onCancel={() => setViewing(false)}/>
            )}
        </>
    )
}

// Card preview cho 1 lớp trong "Các Lớp Tuyển Sinh" - thay hẳn tab "Lớp học" cũ (đã bỏ khỏi
// courseRouteEntities.js, không còn CRUD/xem nhanh ở đây, chỉ đọc). KHÔNG có nút đăng ký riêng
// từng lớp - đăng ký khóa học dùng chung 1 nút ở CourseBanner.js/CourseSubscriptionBar.js.
// Lịch học lấy timetable ĐẦU TIÊN của lớp (item.timetables - CourseClassTransformerTiny.php) -
// đa số lớp chỉ có 1 khung giờ lặp lại/tuần, khớp đúng 1 dòng hiển thị như mockup.
const CourseClassPreviewCard = ({item}) => {
    const {course_class_idcode, timetables, dedicated_teacher} = item ?? {}
    const {days_of_week, from_time, to_time, location_address} = _.first(timetables) ?? {}

    const formatTime = (time) => {
        const parsed = dayjs(time, 'HH:mm:ss')
        return parsed.isValid() ? parsed.format('HH:mm') : null
    }
    const timeLabel = _.some([formatTime(from_time), formatTime(to_time)])
        ? `${formatTime(from_time) ?? '?'} - ${formatTime(to_time) ?? '?'}`
        : null

    return (
        <div className="p-3 md:p-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-slate-50 rounded-xl">
            <div className="flex flex-col gap-1 flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    {renderIf(
                        course_class_idcode,
                        <span className="font-mono font-semibold text-2xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded flex-shrink-0">
                            {course_class_idcode}
                        </span>
                    )}
                    <Link to={getLinkToDetail(item)} className="font-bold text-sm text-color-000 no-underline">{getTitle(item)}</Link>
                </div>
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-color-300">
                    {renderIf(
                        days_of_week && timeLabel,
                        <span className="flex items-center gap-1">
                            <MdAccessTime size={12} className="text-orange-400 flex-shrink-0"/>
                            {renderDayOfWeek(days_of_week)} • {timeLabel}
                        </span>
                    )}
                    {renderIf(
                        location_address,
                        <span className="flex items-center gap-1">
                            <FiMapPin size={12} className="text-primary-400 flex-shrink-0"/>
                            {location_address}
                        </span>
                    )}
                    {/* dedicated_teacher - BE tự suy khi lớp có ĐÚNG 1 môn học + ĐÚNG 1 giảng viên dạy
                        môn đó qua thời khoá biểu (xem CourseClassTransformer::getDedicatedSubjectTeacher).
                        Coi như giáo viên dedicate cho cả lớp, hiện ngay ở card preview này (bạn yêu
                        cầu) - không cần bấm vào lớp mới biết ai dạy. */}
                    {renderIf(
                        dedicated_teacher,
                        <span className="flex items-center gap-1">
                            <FaUserTie size={11} className="text-indigo-400 flex-shrink-0"/>
                            {getTitle(dedicated_teacher)}
                        </span>
                    )}
                </div>
            </div>
        </div>
    )
}

// Chip preview cho 1 người trong Ban giảng huấn (course_teachers) - rút gọn từ renderItem đầy đủ
// của CourseTeacherTab.js (không có action sửa/xóa - đây chỉ là preview trong About, CRUD thật
// vẫn ở tab "Ban điều hành").
const CourseTeacherChip = ({item}) => {
    const {teacher, teacher_name, position} = item ?? {}
    return (
        <div className="p-3 md:p-4 flex items-center gap-3 border border-color-100 background rounded-xl">
            <Avatar
                size={44}
                src={getAvatar(teacher)}
                icon={<FaUserTie size={18} className="text-color-500"/>}
                style={{border: '2px solid #4ade80'}}
                className="flex flex-center background-200 flex-shrink-0"
            />
            <div className="flex flex-col min-w-0">
                <span className="font-bold text-sm text-color-000 truncate">
                    {teacher?.patron_saint && (
                        <span className="mr-1 font-medium text-color-300">
                            {getAliasName(teacher, 'patron_saint')}
                        </span>
                    )}
                    {teacher?.prefix_title} {getTitle(teacher) || teacher_name}
                </span>
                {renderIf(
                    position,
                    <span className="text-xs text-color-300 truncate">{position}</span>
                )}
            </div>
        </div>
    )
}

const AdminWalkInFrame = ({courseId, subjects, metadata}) => {
    const t = useTranslate()
    const [open, setOpen] = useState(false)
    return (
        <div className="flex flex-col gap-3 p-4 rounded-2xl border border-indigo-100 bg-indigo-50">
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <PiPencilSimpleLineFill size={18} className="text-indigo-500"/>
                </div>
                <div className="flex flex-col gap-0.5 flex-1">
                    <span className="text-sm font-bold text-indigo-700 tracking-wide">
                        {t('admin registration')}
                    </span>
                    <span className="text-xs text-indigo-400 italic leading-snug">
                        {t('admin can register students on behalf — walk in enrollment for new or existing students')}
                    </span>
                </div>
            </div>
            <button
                onClick={() => setOpen(true)}
                className="w-full h-10 rounded-xl font-semibold text-sm text-white bg-indigo-500 hover:bg-indigo-600 transition-colors">
                {t('walk in enrollment')}
            </button>
            <WalkInEnrollmentModal
                open={open}
                courseId={courseId}
                subjects={subjects}
                metadata={metadata}
                onClose={() => setOpen(false)}
                onCreated={() => setOpen(false)}
            />
        </div>
    )
}

const CourseInfoTab = () => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)
    const {item, renderWarnings = NullAsDefault} = useContext(CourseContext)
    const {
        medal, about, description, time_open, time_close, last_enrollment_time,
        academic_field, academic_year, edit, subjects, course_teachers, course_classes, student_quota, student_count,
        graduation_criteria,
    } = item ?? {}
    const contentRef = useRef(null)
    const [isExpanded, setIsExpanded] = useState(false)
    const [showToggle, setShowToggle] = useState(false)

    const warnings = Array.from(item?.warnings ?? [])
    const is_visitor = warnings.length

    useEffect(() => {
        const el = contentRef.current
        if (!el) return

        const checkOverflow = () => {
            const maxHeightPx = window.innerHeight * 0.4
            const hasOverflow = el.scrollHeight > maxHeightPx + 1
            setShowToggle(hasOverflow)
            if (!hasOverflow) {
                setIsExpanded(false)
            }
        }

        checkOverflow()
        const resizeObserver = new ResizeObserver(checkOverflow)
        resizeObserver.observe(el)
        window.addEventListener('resize', checkOverflow)

        return () => {
            resizeObserver.disconnect()
            window.removeEventListener('resize', checkOverflow)
        }
    }, [about])

    if (is_visitor) {
        return (
            <div className="w-full flex flex-col gap-3">
                {renderWarnings(warnings)}
            </div>
        )
    }

    const hasDates = _.some([time_open, time_close, last_enrollment_time])

    const hasAboutContent = !isRawContentEmpty(about)
    const hasSubjects = Array.from(subjects ?? []).length > 0
    const hasCourseClasses = Array.from(course_classes ?? []).length > 0
    const hasCourseTeachers = Array.from(course_teachers ?? []).length > 0

    return renderIf(
        item,
        <div className="w-full flex flex-col gap-6">

            {/* ── About course — chỉ hiện khi có nội dung thật (badge/mô tả/giới thiệu) ── */}
            {renderIf(
                hasAboutContent,
                <div
                    className="p-3 md:p-4 flex flex-col gap-3 background border border-color-50 shadow-sm rounded-2xl md:rounded-3xl">
                    <SectionEyebrowHeader
                        icon={<BsInfoCircle size={16}/>}
                        iconClassName="bg-slate-100 text-slate-500"
                        eyebrow="overview"
                        eyebrowClassName="text-slate-500"
                        title="about course"
                    />

                    {renderIf(
                        !isRawContentEmpty(about),
                        <div className="flex flex-col items-center gap-3">
                            <div
                                ref={contentRef}
                                // style={{
                                //     maxHeight: showToggle && !isExpanded ? '40vh' : 'none',
                                // }}
                                className="w-full overflow-hidden relative transition-all duration-300">
                                <div className="leading-7 text-sm text-color-200" style={{lineHeight: '1.75'}}>
                                    <CustomEditorRenderer
                                        rawValue={about}
                                        className="border-transparent"
                                    />
                                </div>
                                {/*{showToggle && !isExpanded && (*/}
                                {/*    <div*/}
                                {/*        style={{*/}
                                {/*            position: 'absolute',*/}
                                {/*            bottom: 0,*/}
                                {/*            left: 0,*/}
                                {/*            right: 0,*/}
                                {/*            height: 80,*/}
                                {/*            background: 'linear-gradient(transparent 0%, var(--background-50) 95%)',*/}
                                {/*        }}*/}
                                {/*    />*/}
                                {/*)}*/}
                            </div>
                            {/*{showToggle && (*/}
                            {/*    <button*/}
                            {/*        type="button"*/}
                            {/*        onClick={() => setIsExpanded(!isExpanded)}*/}
                            {/*        style={{padding: '0.25rem 0.75rem'}}*/}
                            {/*        className="w-fit background-200 hover:bg-secondary-50 rounded-lg text-color-300 hover:text-secondary-600 tracking-wide">*/}
                            {/*        {isExpanded ? t('see less') : t('see more')}*/}
                            {/*    </button>*/}
                            {/*)}*/}
                        </div>
                    )}
                </div>
            )}

            {/* ── Khung chương trình — danh sách môn học, chỉ hiện khi có môn học ── */}
            {renderIf(
                hasSubjects,
                <div
                    className="p-3 md:p-4 flex flex-col gap-3 background border border-color-50 shadow-sm rounded-2xl md:rounded-3xl">
                    <SectionEyebrowHeader
                        icon={<TbListDetails size={16}/>}
                        iconClassName="bg-indigo-100 text-indigo-600"
                        eyebrow="curriculum framework"
                        eyebrowClassName="text-indigo-500"
                        title="subject list and details"
                        manageLink={edit ? `${getLinkToDetail(item)}/subjects` : undefined}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {subjects.map((courseSubject, index) => (
                            <CourseSubjectPreviewCard key={index} index={index} item={courseSubject}/>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Các lớp tuyển sinh — thay tab "Lớp học" cũ đã bỏ, chỉ hiện khi có lớp ── */}
            {renderIf(
                hasCourseClasses,
                <div
                    className="p-3 md:p-4 flex flex-col gap-3 background border border-color-50 shadow-sm rounded-2xl md:rounded-3xl">
                    <SectionEyebrowHeader
                        icon={<HiOutlineAcademicCap size={16}/>}
                        iconClassName="bg-blue-100 text-blue-600"
                        eyebrow="enrolling classes"
                        eyebrowClassName="text-blue-500"
                        title="class list and schedule details"
                    />
                    <div className="flex flex-col gap-3">
                        {course_classes.map((courseClass, index) => (
                            <CourseClassPreviewCard key={index} item={courseClass}/>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Ban giảng huấn — giảng viên/linh mục phụ trách, chỉ hiện khi có người được gán ── */}
            {renderIf(
                hasCourseTeachers,
                <div
                    className="p-3 md:p-4 flex flex-col gap-3 background border border-color-50 shadow-sm rounded-2xl md:rounded-3xl">
                    <SectionEyebrowHeader
                        icon={<FaChalkboardTeacher size={15}/>}
                        iconClassName="bg-green-100 text-green-600"
                        eyebrow="teaching staff"
                        eyebrowClassName="text-green-600"
                        title="teachers and instructors in charge"
                        manageLink={edit ? `${getLinkToDetail(item)}/teachers` : undefined}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {course_teachers.map((courseTeacher, index) => (
                            <CourseTeacherChip key={index} item={courseTeacher}/>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Meta card ── */}
            <div
                className="p-3 md:p-4 flex flex-col gap-3 background border border-color-50 shadow-sm rounded-2xl md:rounded-3xl">
                <div className="flex flex-col gap-6">
                    <div className="p-3 bg-slate-50 rounded-xl flex flex-col gap-3">
                        <span className="text-2xs font-bold text-slate-400 uppercase tracking-widest flex-shrink-0">
                            {t('time')}
                        </span>
                        {renderIf(
                            hasDates,
                            <CourseDateTimeline
                                time_open={time_open}
                                time_close={time_close}
                                last_enrollment_time={last_enrollment_time}
                                student_quota={student_quota}
                                student_count={student_count}
                            />
                        )}
                    </div>
                    {/* ── Share / QR — QR bên trái, mô tả + link bên phải cho cân đối (box giờ full-width) ── */}
                    <div className="p-3 bg-slate-50 rounded-xl flex flex-col gap-3">
                        <span className="text-2xs font-bold text-slate-400 uppercase tracking-widest flex-shrink-0">
                            {t('share course')}
                        </span>
                        {/* items-center (bỏ tiền tố sm:) - mobile xếp cột (flex-col) vẫn cần center
                            NGANG cho QR, không chỉ center DỌC lúc lên hàng ngang ở sm+ như trước (mặc
                            định align-items:stretch của flex-col khiến QR dính lề trái). */}
                        <div className="flex flex-col items-center sm:flex-row gap-4">
                            <QRComponent encode={getAbsoluteLinkToDetail(item)} defaultMaxSize={150}/>
                            <div className="flex flex-col gap-2 flex-1 min-w-0">
                                <span className="text-xs text-color-300 tracking-wider">
                                    {t('access page by scanning the QR code')}
                                </span>
                                <div className="pt-2 border-t border-slate-200 text-xs text-color-300 italic tracking-wider">
                                    {t('or copy and share this link with anyone you\'d like to invite')}
                                </div>
                                <div
                                    onClick={() => copyLink(getAbsoluteLinkToDetail(item))}
                                    style={{
                                        padding: '0.5rem 0.75rem',
                                        background: '#fff',
                                        borderRadius: '0.5rem',
                                        cursor: 'pointer'
                                    }}
                                    className="flex items-center gap-2 border border-color-100 hover:border-secondary-400 transition-all duration-300">
                                    <span className="flex-1 text-xs text-color-200 font-medium italic truncate">
                                        {truncateMiddle(getAbsoluteLinkToDetail(item), isSm ? 36 : 56)}
                                    </span>
                                    <IoIosCopy size={13} className="text-color-400 hover:text-secondary-500 flex-shrink-0"/>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Certificate + graduation criteria — 2 cột nếu có đủ cả 2 (bạn yêu cầu "1 bên là
                        bằng, 1 bên là tiêu chuẩn xét"), field nào thiếu thì cột đó ẩn (không hiện tiêu
                        đề rỗng), cả khối ẩn hẳn nếu thiếu cả 2. */}
                    {renderIf(
                        medal || graduation_criteria,
                        <div className={classNames('grid gap-4', medal && graduation_criteria ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1')}>
                            {renderIf(
                                medal,
                                <div className="flex flex-col gap-3">
                                    <span
                                        className="text-2xs font-bold text-slate-400 uppercase tracking-widest leading-none flex-shrink-0">
                                        {t('certificates')}
                                    </span>
                                    {medal?.title && (
                                        <span className="flex items-center gap-1 leading-snug">
                                            <PiMedalFill size={14} style={{color: '#ca8a04', flexShrink: 0}}/>
                                            <span style={{color: '#92400e'}} className="font-semibold text-base leading-snug">
                                                {medal?.title}
                                            </span>
                                        </span>
                                    )}
                                    {!!medal?.avatar && (
                                        <Image
                                            src={medal?.avatar}
                                            style={{maxHeight: 240, minHeight: 80, display: 'block'}}
                                            className="w-full rounded-xl object-contain"
                                            wrapperClassName="w-full border border-color-50 rounded-xl"
                                            preview={{
                                                mask: (
                                                    <div className="flex flex-col items-center gap-1">
                                                        <HiViewfinderCircle size={22} className="text-white"/>
                                                        <span className="text-xs text-white font-medium opacity-90">
                                                                    {t('tap to view')}
                                                                </span>
                                                    </div>
                                                ),
                                            }}
                                        />
                                    )}
                                </div>
                            )}
                            {renderIf(
                                graduation_criteria,
                                <div className="flex flex-col gap-3">
                                    <span
                                        className="text-2xs font-bold text-slate-400 uppercase tracking-widest leading-none flex-shrink-0">
                                        {t('graduation criteria')}
                                    </span>
                                    <p className="text-sm text-color-200 leading-relaxed whitespace-pre-line">
                                        {graduation_criteria}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>


            {/* ── Admin walk-in frame — chỉ hiện với admin ── */}
            {renderIf(
                edit,
                <AdminWalkInFrame
                    courseId={item?.id}
                    subjects={subjects ?? []}
                    metadata={item?.metadata}
                />
            )}
        </div>
    )
}

export default CourseInfoTab
